// Find candidate authoring turns through normalized, indexed conversation text.

import { extractFileOps } from "./traceFileTree.js";

// "name + at least parent path": the target's last two path segments, so a
// match holds across different checkout roots (…/src/identity_match.rs) but
// won't collide with a same-named file in a different directory.
export function targetSuffix(path) {
  const segs = String(path || "").replace(/\\/g, "/").split("/").filter(Boolean);
  return segs.slice(-2).join("/");
}

export function pathMatchesSuffix(candidatePath, suffix) {
  if (!candidatePath || !suffix) return false;
  return String(candidatePath).replace(/\\/g, "/").endsWith(suffix);
}

export function lineBlameSpec(line, { startIso, endIso, limit = 200 } = {}) {
  const escaped = String(line).replace(/[\\%_]/g, (m) => `\\${m}`);
  const field = (name) => ({ stepAlias: "s", field: `DIMENSION_FIELD_CONVERSATION_SEARCH_${name}` });
  return {
    root: { alias: "s", entity: "ENTITY_TYPE_CONVERSATION_SEARCH_CONTENT", children: [] },
    timeRange: { absolute: { start: startIso, end: endIso } },
    filter: { pred: { field: field("TEXT"), op: "OPERATOR_ILIKE", value: `%${escaped}%` } },
    output: { dimensions: [
      ["SOURCE_EVENT_ID", "event_id"], ["ENDPOINT_ID", "endpoint_id"], ["TRACE_ID", "session_id"], ["EVENT_TIME", "event_time"],
    ].map(([name, outputAlias]) => ({ field: field(name), outputAlias })) },
    orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_ASC" }], limit,
  };
}

const DAY_MS = 86400 * 1000;

// Whether a reconstructed line is blame-able: a real file path and non-blank
// text. (The component ANDs this with the presence of an onBlameLine handler.)
export function canBlameLine(entry, filePath) {
  return Boolean(filePath) && new TextEncoder().encode(String(entry?.text || "").trim()).length >= 3;
}

// The LineBlamePanel state machine as a PURE classifier: map the panel's
// (status, hit, capped) into exactly one of five view kinds. Kept here so the
// branch selection is unit-tested even though the panel's JSX isn't rendered
// in tests. running → still walking; error → a query failed; hit → author
// found; capped → hit the hydration cap with no author; no-hit → window
// exhausted with no authoring write.
export function blameViewKind(state) {
  if (!state || state.status === "running") return "running";
  if (state.status === "error") return "error";
  if (state.hit) return "hit";
  if (state.capped) return "capped";
  return "no-hit";
}

// Bound the blame ILIKE window from the dashboard's selected range: honor a
// sane relative duration, but cap at 1 week and default to 1 week. The id
// query is paged one day at a time (~a bounded blob scan each), so the window
// is just how many day-scans a not-found search will walk — 7d keeps it
// tractable.
export function blameRangeSeconds(range) {
  const d = Number(range?.relative?.durationSeconds);
  if (Number.isFinite(d) && d > 0) return Math.min(d, 7 * 86400);
  return 7 * 86400;
}

// Shared inner: given already-extracted ops, return the op that authored the
// line into a path-matching file (or null). Used by authoringOpForLine (ops
// from hydrated turns) AND by the fs-signal blame (ops from fs.write values),
// so both classify identically. Trailing-whitespace-insensitive: the
// reconstructed line the caller passes can carry different trailing spaces than
// the raw write (numbered-read stripping, editor trimming), and that shouldn't
// miss.
export function authoringOpFromOps(ops, line, targetPath) {
  const suffix = targetSuffix(targetPath);
  const needle = String(line).trimEnd();
  const has = (text) => String(text || "").split("\n").some((ln) => ln.trimEnd() === needle);
  for (const op of ops || []) {
    if (!op.ok || !pathMatchesSuffix(op.path, suffix)) continue;
    if (op.kind === "write" && has(op.content)) return op;
    if (op.kind === "edit" && (op.hunks || []).some((h) => has(h.new))) return op;
  }
  return null;
}

// Client-side filter: does this hydrated event's tool activity actually WRITE
// (or edit-in) the exact line into a path-matching file? Returns the matching
// op or null. `hydrated.turns` is the parseApiRequest output; we reuse the
// shipped extractFileOps so classification matches the reconstruction exactly.
export function authoringOpForLine(hydrated, line, targetPath) {
  let ops = [];
  try { ops = extractFileOps(hydrated?.turns); } catch { return null; }
  return authoringOpFromOps(ops, line, targetPath);
}

// Orchestrate the whole blame, PAGED so it can't crash the tab: walk the
// window one day-sized chunk at a time (oldest first, so the earliest author
// surfaces before we scan later days), run that chunk's candidate query, and
// hydrate its hits ONE AT A TIME — stopping at the first authoring write, at
// the hydration cap, or on abort. Each event is hydrated at most once
// (dedup), and the cap bounds memory (hydrated bodies are ~large). `runRows`
// / `hydrate` are injected (runQueryRows / hydratePrompt); `nowMs` is
// injectable for tests. Reports onProgress({ scanned, day, days }).
// Walk the window ONE DAY AT A TIME, oldest first. Per day: (1) one small
// id-only query (its blob scan bounded to that day, so the gateway can't
// collapse), then (2) hydrate that day's candidate bodies ONE AT A TIME,
// stopping at the first authoring write. Nothing scans more than a day;
// nothing fetches more than one body at a time. A hard cap bounds total
// hydrations. `runRows`/`hydrate` injected; `nowMs` injectable for tests.
// Reports onProgress({ scanned, day, days }).
export async function blameLine({
  line, targetPath, runRows, hydrate,
  durationSeconds = 7 * 86400, nowMs = Date.now(),
  maxHydrate = 40, perDayLimit = 200, signal, onProgress,
}) {
  const endMs = nowMs;
  const startMs = endMs - durationSeconds * 1000;
  const chunks = [];
  for (let t = startMs; t < endMs; t += DAY_MS) chunks.push([t, Math.min(t + DAY_MS, endMs)]);
  const iso = (ms) => new Date(ms).toISOString();

  const days = chunks.length;
  const seen = new Set();
  let scanned = 0;
  for (let ci = 0; ci < chunks.length; ci++) {
    if (signal?.aborted) return { aborted: true, scanned, days };
    const [cs, ce] = chunks[ci];
    // (1) one bounded id-only query for this day.
    let rows;
    try {
      rows = await runRows(lineBlameSpec(line, { startIso: iso(cs), endIso: iso(ce), limit: perDayLimit }));
    } catch (err) {
      if (signal?.aborted) return { aborted: true, scanned, days };
      return { hit: null, scanned, days, error: err?.message || String(err) };
    }
    const candidates = (rows || [])
      .map((r) => ({
        eventId: String(r.event_id),
        sessionId: r.session_id != null ? String(r.session_id) : "",
        endpointId: r.endpoint_id != null ? String(r.endpoint_id) : "",
        ts: r.event_time,
      }))
      .filter((c) => c.eventId && !seen.has(c.eventId))
      .sort((a, b) => new Date(a.ts) - new Date(b.ts));
    // (2) hydrate this day's bodies one at a time.
    for (const c of candidates) {
      if (signal?.aborted) return { aborted: true, scanned, days };
      if (scanned >= maxHydrate) return { hit: null, scanned, days, capped: true };
      seen.add(c.eventId);
      scanned++;
      onProgress?.({ scanned, day: ci + 1, days });
      let hydrated = null;
      try { hydrated = await hydrate(c.eventId); } catch { continue; }
      const op = authoringOpForLine(hydrated, line, targetPath);
      if (op) return { hit: { ...c, kind: op.kind, path: op.path }, scanned, days };
    }
  }
  return { hit: null, scanned, days };
}
